import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CreateCompanyFormValues } from "@src/pages/super-task-manager/creation-drawers/types.data";
import { Company } from "@src/pages/super_task_manager/companies/CompaniesListTable";
import { OverallPerformanceResponse } from "./CategoryQuestionsApiSlice";
import { APIResponse } from "@src/types/CommonTypes";

const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_CHALLENGE_BASE_URL;

export interface GetCategoryCompanyApiResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    content: Company[];
  };
}

export interface GetCompaniesForQuestionApiResponse extends APIResponse {
  response: { challengeCount: number; id: string; name: string }[];
}

export const CategoryCompanyApiSlice = createApi({
  reducerPath: "categoryCompanyApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ["getCategoryCompany"],
  endpoints: (builder) => ({
    getCategoryCompany: builder.query<
      GetCategoryCompanyApiResponse,
      { categoryId?: string; pageNo: number; pageSize: number }
    >({
      query: ({ categoryId, pageNo, pageSize }) => {
        const params = new URLSearchParams({
          pageNo: pageNo.toString(),
          pageSize: pageSize.toString(),
        });

        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        return {
          url: `company/get-all?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["getCategoryCompany"],
    }),
    addCategoryCompany: builder.mutation<
      { httpStatus: string; statusCode: number },
      { payload: CreateCompanyFormValues }
    >({
      query: ({ payload }) => {
        return {
          url: `company`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getCompaniesCount: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { id: string; name: string }[];
      },
      void
    >({
      query: () => {
        return {
          url: `challenge/companies-count`,
          method: "GET",
        };
      },
    }),
    getCompaniesForQuestion: builder.query<
      GetCompaniesForQuestionApiResponse,
      { challengeId: number }
    >({
      query: ({ challengeId }) => ({
        url: `company/by-challenge-id-mapped?challengeId=${challengeId}`,
        method: "GET",
      }),
    }),
    getUnassignedCompaniesForQuestion: builder.query<
      GetCompaniesForQuestionApiResponse,
      { challengeId: number | string }
    >({
      query: ({ challengeId }) => ({
        url: `company/by-challenge-id-unmapped?challengeId=${challengeId}`,
        method: "GET",
      }),
    }),
    addCompaniesForQuestion: builder.mutation<
      any,
      { challengeId: number | string | string[]; companyIds: string[] }
    >({
      query: ({ challengeId, companyIds }) => ({
        url: `challenge/assign-companies-challenge?challengeId=${challengeId}`,
        method: "PUT",
        body: companyIds,
      }),
    }),
    getCompanyById: builder.query<
      any,
      {
        categoryId: string;
        companyId: string;
        pageNo: number;
        pageSize: number;
        userId?: string;
      }
    >({
      query: ({ categoryId, companyId, pageNo, pageSize, userId }) => {
        const params: Record<string, any> = {
          categoryId,
          companyId,
          pageNo,
          pageSize,
        };

        if (userId) {
          params.userId = userId;
        }

        return {
          url: `company`,
          method: "GET",
          params,
        };
      },
    }),
    getQuestionCountTopics: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { [key: string]: number | string };
      },
      { userId: string; topicId: string }
    >({
      query: ({ userId, topicId }) => ({
        url: `challenge-topic/get-overall-performance-by-topic?userId=${userId}&topicId=${topicId}`,
        method: "GET",
      }),
    }),
    getQuestionCountCompanies: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { [key: string]: number | string };
      },
      { userId: string; companyId: string }
    >({
      query: ({ userId, companyId }) => ({
        url: `company/get-overall-performance-by-company?userId=${userId}&companyId=${companyId}`,
        method: "GET",
      }),
    }),
    getOverallPerformanceForCompany: builder.query<
      OverallPerformanceResponse,
      { userId: string; companyId: string; categoryId: string }
    >({
      query: ({ userId, companyId, categoryId }) => ({
        url: `company/get-overall-performance-by-company?userId=${userId}&companyId=${companyId}&categoryId=${categoryId}`,
        method: "GET",
      }),
    }),
    getOverallPerformanceForTopic: builder.query<
      OverallPerformanceResponse,
      { userId: string; topicId: string }
    >({
      query: ({ userId, topicId }) => ({
        url: `challenge-topic/get-overall-performance-by-topic?userId=${userId}&topicId=${topicId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetCategoryCompanyQuery,
  useLazyGetCompaniesCountQuery,
  useLazyGetCategoryCompanyQuery,
  useAddCategoryCompanyMutation,
  useGetCompaniesForQuestionQuery,
  useGetUnassignedCompaniesForQuestionQuery,
  useAddCompaniesForQuestionMutation,
  useLazyGetCompanyByIdQuery,
  useLazyGetQuestionCountTopicsQuery,
  useLazyGetOverallPerformanceForCompanyQuery,
  useLazyGetOverallPerformanceForTopicQuery,
  useLazyGetQuestionCountCompaniesQuery,
} = CategoryCompanyApiSlice;
